// src/components/email/UserMessages.tsx
import React, { useState, useEffect, useMemo } from 'react';
import {
  gql,
  useQuery,
  useMutation,
  useLazyQuery,
  ApolloError,
  OperationVariables,
  ApolloQueryResult,
} from '@apollo/client';
import { BookOpen, Users } from 'lucide-react';

import { Sidebar } from './components/Sidebar';
import { ThreadList } from './components/ThreadList';
import { ThreadDetail } from './components/ThreadDetail';
import { ComposeModal } from './components/ComposeModal';
import { ReplyModal } from './components/ReplyModal';
import { AddressBook } from './components/AddressBook';
//import { TwoFactorLogin } from './components/TwoFactorLogin'; -- it was a bad idea 

const availableContacts = [
  { email: 'test@test.com', name: 'Test User' },
  { email: 'test2@test.com', name: 'Test User 2' },
];

// Types
export interface UserMessage {
  id: string;
  senderId?: number | null;
  senderString?: string | null;
  recipientId: number;
  recipientString?: string | null;
  subject: string;
  body: string;
  rawBody?: string | null;
  isRead: boolean;
  isPinned: boolean;
  isDeleted: boolean;
  createdAt: string;
  toJson?: string[] | undefined;
  ccJson?: string[] | undefined;
  replyJson?: string[] | undefined;
  tags?: string;
  recipients?: string[];
}

interface ListInboxData {
  listUserInboxMessages: UserMessage[];
  listUserInboxMessagesByToken: UserMessage[];
}

interface State {
  loading: boolean;
  error: ApolloError | null;
  data: UserMessage[] | null;
  threads: UserMessage[];
  totalCount: number;
}

export const UserMessages = ({
  userData,
  userEmail,
  userTags,
  hooks,
  logout
} :any) => {
  const { markRead, deleteMessage, respondMessage, sendMessage, getThread, listInbox, updateUser } = hooks;
  const [state, setState] = useState<any>({ loading: false, error: null, data: null, threads: [], totalCount: 0 });


  const [currentSidebarSection, setCurrentSidebarSection] = useState({ folder: 'inbox', filter: 'unread'}) as any;
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [isSearching, setIsSearching] = useState<boolean>(false);
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>('');

  const [limitState, setLimitState] = useState<number>(20);
  const [pageState, setPageState] = useState<number>(1);

  const [selectedMessage, setSelectedMessage] = useState<any>(null);
  const [isReplyOpen, setIsReplyOpen] = useState(false);
  const [replyBody, setReplyBody] = useState('');
  const [replyComposeMessage, setReplyComposeMessage] = useState<UserMessage | null>(null);
  const [isAddressBookOpen, setIsAddressBookOpen] = useState(false);
  const [userAddresses, setUserAddresses] = useState<any[]>([]);

  // Mobile view state
  const [showSidebar, setShowSidebar] = useState(false);
  const [showMessageList, setShowMessageList] = useState(true);
  const [showMessageDetail, setShowMessageDetail] = useState(false);

  // Initialize addresses from userData
  useEffect(() => {
    setUserAddresses(userData.addresses || []);
  }, [userData.addresses]);
 
  const handlePageChange = (newPage: number) => {
    setPageState(newPage);
    // The useEffect will automatically trigger a refetch when pageState changes
  };

  const handleLimitChange = (newLimit: number) => {
    setLimitState(newLimit);
    setPageState(1); // Reset to first page when limit changes
  };

  const handleSearch = () => {
    // Reset to first page when searching
    setPageState(1);
    setIsSearching(true);
    // Force immediate search by setting debounced term
    setDebouncedSearchTerm(searchTerm);
    // Clear selected message when searching
    setSelectedMessage(null);
  };  const { folder, filter } = currentSidebarSection;

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      // Clear selected message when search term changes
      if (searchTerm.trim() !== debouncedSearchTerm.trim()) {
        setSelectedMessage(null);
      }
    }, 500); // 500ms debounce

    return () => clearTimeout(timer);
  }, [searchTerm, debouncedSearchTerm]);

  useEffect(() => {
    const fetchMessages = async () => {
      setState( (prevState:any) => ({ ...prevState, loading: true }));
      try {
        const offset = (pageState - 1) * limitState;
        // When searching, default to "all" messages to show all search results
        const searchFilter = debouncedSearchTerm.trim() ? `${folder}:all` : `${folder}:${filter}`;
        const result = await listInbox(debouncedSearchTerm, limitState, offset, searchFilter);
        
        // Handle both successful responses and undefined/null data
        const data = result?.data;
        
        if (!data) {
          console.warn('No data returned from listInbox query');
          setState( (prevState:any) => ({ 
            ...prevState, 
            loading: false, 
            threads: [], 
            totalCount: 0,
          }));
          return;
        }

        // Extract messages and totalCount from various possible response structures
        let messages: UserMessage[] = [];
        let totalCount: number = 0;

        // Handle the standard response format
        if (data.listUserInboxMessages) {
          messages = data.listUserInboxMessages.messages || [];
          totalCount = parseInt(data.listUserInboxMessages.totalCount) || 0;
        } else if (data.listUserInboxMessagesByToken) {
          messages = data.listUserInboxMessagesByToken.messages || [];
          totalCount = parseInt(data.listUserInboxMessagesByToken.totalCount) || 0;
        }
        // Handle sent messages response format
        else if (data.listUserSentMessages) {
          messages = data.listUserSentMessages.messages || [];
          totalCount = parseInt(data.listUserSentMessages.totalCount) || 0;
        } else if (data.listUserSentMessagesByToken) {
          messages = data.listUserSentMessagesByToken.messages || [];
          totalCount = parseInt(data.listUserSentMessagesByToken.totalCount) || 0;
        }
        // Handle search response formats
        else if (data.searchUserInboxMessages) {
          messages = data.searchUserInboxMessages.messages || [];
          totalCount = parseInt(data.searchUserInboxMessages.totalCount) || 0;
        } else if (data.searchUserInboxMessagesByToken) {
          messages = data.searchUserInboxMessagesByToken.messages || [];
          totalCount = parseInt(data.searchUserInboxMessagesByToken.totalCount) || 0;
        }

        setState( (prevState:any) => ({ 
          ...prevState, 
          loading: false, 
          threads: messages, 
          totalCount: totalCount,
        }));
      } catch (error) {
        console.error('Error fetching messages:', error);
        setState( (prevState:any) => ({ 
          ...prevState, 
          loading: false, 
          error: error as ApolloError,
          threads: [],
          totalCount: 0
        }));
      } finally {
        setIsSearching(false);
      }
    };

    fetchMessages();
  }, [debouncedSearchTerm, limitState, pageState, folder, filter]);



  const user = useMemo(() => {
    try {
      const tags = JSON.parse(userData.tags||'{}');
 
      const rules = JSON.parse(userData.rules||'[]');
      return {
        addresses: userAddresses || [],
        name: userData.name || '',
        folders: tags.folders || [],
        rules: rules || [],
      };
    } catch (error) {
      console.error(error);
      return {
        addresses: [],
        folders: [],
        rules: [],
      };
    }
    
  }, [userAddresses, userData]);

  //console.log("user", user);

  const handleMessageSelect = (message: any) => {
    setSelectedMessage(message);
    setShowMessageDetail(true);
    setShowMessageList(false);
  };

  const handleBackToList = () => {
    setShowMessageDetail(false);
    setShowMessageList(true);
    setSelectedMessage(null);
  };

  const handleBackToSidebar = () => {
    setShowMessageList(false);
    setShowSidebar(true);
  };

  return (
    <div className="flex flex-col h-screen bg-gray-50 text-sm">
      <header className="bg-white border-b shadow-sm">
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center">
            {showMessageDetail && (
              <button
                onClick={handleBackToList}
                className="mr-3 p-1 text-gray-600 hover:text-gray-800"
              >
                ←
              </button>
            )}
            {showMessageList && (
              <button
                onClick={handleBackToSidebar}
                className="mr-3 p-1 text-gray-600 hover:text-gray-800 md:hidden"
              >
                ☰
              </button>
            )}
            <h1 className="text-lg font-semibold text-gray-800">Internal Mail</h1>         
          </div>
          <div className="flex items-center gap-2">
            <button
              aria-label="Open address book"
              onClick={() => setIsAddressBookOpen(true)}
              className="text-blue-600 p-2"
            >
              <BookOpen size={20} />
            </button>
            {logout && (
              <button
                onClick={() => {
                  document.cookie = 'AuthorizationMailSystem=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
                  window.location.reload();
                }}
                className="text-red-600 hover:text-red-700 text-sm px-2"
              >
                Logout
              </button>
            )}
          </div>
        </div>
        
        {/* Mobile Search - Only show when not viewing message detail */}
        {!showMessageDetail && (
          <div className="px-4 py-3 border-t border-gray-100">
            <div className="text-xs text-gray-600 mb-2 truncate">
              {userEmail}
            </div>
            <div className="flex gap-2">
              <div className="relative flex-1">
                <input
                  type="text"
                  placeholder="Search messages…"
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleSearch();
                    }
                  }}
                  className="w-full h-9 px-3 pr-8 border border-gray-200 rounded-lg shadow-sm bg-gray-50 focus:bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:outline-none text-sm placeholder-gray-500 transition-all duration-200"
                />
                {searchTerm && (
                  <button
                    onClick={() => {
                      setSearchTerm('');
                      setDebouncedSearchTerm('');
                      setPageState(1);
                      setSelectedMessage(null);
                    }}
                    className="absolute right-2 top-1/2 transform -translate-y-1/2 w-5 h-5 flex items-center justify-center text-gray-400 hover:text-gray-600"
                  >
                    ×
                  </button>
                )}
              </div>
              <button
                onClick={handleSearch}
                disabled={isSearching || state.loading}
                className="h-9 px-4 bg-blue-500 text-white rounded-lg shadow-sm hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 text-sm font-medium"
              >
                {isSearching || state.loading ? '...' : 'Go'}
              </button>
            </div>
          </div>
        )}
      </header>
  
      {/* Mobile Content */}
      <div className="flex flex-1 overflow-hidden">
        {/* Sidebar - Hidden on mobile by default, shown when showSidebar is true */}
        <div className={`${showSidebar ? 'block' : 'hidden'} md:block w-full md:w-52 bg-white border-r`}>
          <Sidebar
            currentSection={currentSidebarSection}
            onChangeSection={opt => {
              setPageState(1);
              setSelectedMessage(null);
              setCurrentSidebarSection({ folder: opt.folder, filter: opt.filter });
              setShowSidebar(false);
              setShowMessageList(true);
            }}
            user={user}
          />
        </div>

        {/* Message List - Show when showMessageList is true */}
        <div className={`${showMessageList ? 'flex' : 'hidden'} md:flex flex-col flex-1 overflow-hidden`}>
          {debouncedSearchTerm.trim() && (
            <div className="bg-blue-50 border-b border-blue-200 px-4 py-2 text-sm text-blue-800 flex-shrink-0">
              Search: "<strong>{debouncedSearchTerm}</strong>" ({state.totalCount})
            </div>
          )}
          <div className="flex-1 overflow-hidden">
            <ThreadList
              threads={state.threads}
              page={pageState}
              limit={limitState}
              totalCount={state.totalCount}
              onPageChange={handlePageChange}
              onLimitChange={handleLimitChange}
              onSelect={handleMessageSelect}
            />
          </div>
        </div>

        {/* Message Detail - Show when showMessageDetail is true */}
        <div className={`${showMessageDetail ? 'block' : 'hidden'} md:block w-full md:flex-1 bg-white overflow-y-auto`}>
          {selectedMessage && (
            <ThreadDetail
              getThread={getThread}
              onDelete={(threadId: string) => {
                deleteMessage(threadId);
                setState((prevState: State) => ({
                  ...prevState,
                  threads: prevState.threads.filter((t: UserMessage) => t.id !== threadId)
                }));
                handleBackToList();
              }}
              onReply={(thread: any) => {
                setReplyComposeMessage(thread);
                setIsReplyOpen(true);
              }}
              thread={selectedMessage}
            />
          )}
          {selectedMessage && <ReplyModal
            isOpen={isReplyOpen}
            onRequestClose={() => setIsReplyOpen(false)}
            replyBody={replyBody}
            setReplyBody={setReplyBody}
            onSubmit={async (threadId, body, recipients) => {
              if (!replyComposeMessage) return;
              await respondMessage(threadId, body, recipients);
              setIsReplyOpen(false);
            }}
            
            thread={selectedMessage}
            availableContacts={availableContacts}
            addresses={user.addresses}
            recipients={replyComposeMessage?.recipients}
          />
          }
        </div>
      </div>
  
      {isAddressBookOpen && (
        <AddressBook
          addresses={user.addresses}
          onClose={() => setIsAddressBookOpen(false)}
          
          onUpdate={(addresses) => {
            // strip out __typename before sending
            const clean = addresses.map(({ name, email }) => ({ name, email }));
            console.log("clean addresses →", clean);
            setUserAddresses(clean);
            updateUser({ addresses: clean });
          }}
        />
      )}
      
    </div>
  );
};
